import { useMutation } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { closeModal, openModal } from 'actions/modals';
import {
  PERSISTENT_RECORD_NO_RECORDER_FALLBACK_SHOWN,
  RECORD_BUTTON_CLICKED,
} from 'constants/events';
import {
  CHECKLIST_RECORD_BUTTON_MODAL,
  SDK_RECORDING_LOADING_MODAL,
} from 'constants/modalTypes';
import { selectUserIsLogged } from 'selectors/user';
import * as analytics from 'utilities/analytics';
import { getFlagValueForUser } from 'utilities/featureFlag';
import * as logger from 'utilities/loggerx';
import { mergeListeners } from 'utilities/merge-listeners';
import { incrementMetric } from 'utilities/metrics';

import CreateAnonPartnerSessionRecordingCache from './CreateAnonPartnerSessionRecordingCache.graphql';
import { CHROME_EXTENSION, DESKTOP, SDK } from './constants';
import {
  startChromeExtensionRecording,
  startDesktopRecording,
  startSDKRecording,
} from './utilities';
import { FIX_PERSISTENT_RECORD_MULTIPLE_RECORDERS_TRIGGERING_NUMBER_VARIANT } from '../../../../shared/constants/featureFlag';

export const PERSISTENT_RECORD_BUTTON = 'persistent-record';
export const SDK_IN_ONBOARDING = 'sdk-in-onboarding';

export default function RecordButton({
  children,
  priorityList = [DESKTOP, CHROME_EXTENSION, SDK],
  source = PERSISTENT_RECORD_BUTTON,
}) {
  const dispatch = useDispatch();
  const isLoggedIn = useSelector(selectUserIsLogged);
  const [count, setCount] = useState(null);
  const initiateNextRecorder = () => {
    if (count < priorityList.length - 1) {
      setCount(count + 1);
    }
  };
  const onClick = () => {
    analytics.track(RECORD_BUTTON_CLICKED, { source });
    incrementMetric('button.click', {
      source,
      isLoggedIn,
    });
    setCount(0);
  };

  const [createAnonPartnerSessionRecordingCache] = useMutation(
    CreateAnonPartnerSessionRecordingCache,
    {
      onCompleted: ({ result }) => {
        if (
          result.__typename !== 'createAnonPartnerSessionRecordingCachePayload'
        ) {
          logger.error(new Error(result.message));
        }
      },
    }
  );

  useEffect(() => {
    if (count === null) {
      return;
    }

    const initiateRecorder = async () => {
      const waitForDesktopInitFromFlag = await getFlagValueForUser(
        FIX_PERSISTENT_RECORD_MULTIPLE_RECORDERS_TRIGGERING_NUMBER_VARIANT
      );

      const recorder = priorityList[count];
      let hasStartedRecorder = null;

      switch (recorder) {
        case DESKTOP: {
          hasStartedRecorder = await startDesktopRecording(
            source,
            waitForDesktopInitFromFlag
          );

          break;
        }

        case CHROME_EXTENSION: {
          hasStartedRecorder = await startChromeExtensionRecording(source);

          break;
        }

        default: {
          hasStartedRecorder = await startSDKRecording({
            source,
            isLoggedIn,
            onRecordingComplete() {
              dispatch(openModal(SDK_RECORDING_LOADING_MODAL));
            },
            onUploadComplete(oembed) {
              dispatch(closeModal());

              if (source === PERSISTENT_RECORD_BUTTON && !isLoggedIn) {
                createAnonPartnerSessionRecordingCache({
                  variables: { videoId: oembed.id },
                });
              }
            },
          });

          if (!hasStartedRecorder) {
            analytics.track(PERSISTENT_RECORD_NO_RECORDER_FALLBACK_SHOWN);
            incrementMetric('recorder_initiate.no_recorder_fallback_modal', {
              source,
            });
            dispatch(
              openModal(CHECKLIST_RECORD_BUTTON_MODAL, {
                source,
              })
            );
          }
        }
      }

      if (hasStartedRecorder) {
        setCount(null);
      } else {
        initiateNextRecorder();
      }
    };

    initiateRecorder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);

  const child = React.Children.only(children);

  return React.cloneElement(child, {
    onClick: mergeListeners(onClick, child.props.onClick),
  });
}
